import { Injectable } from '@angular/core';
import { ApiService } from '../global/services';
import { DataService } from './data.service';

export interface VehicleDataRequest {
  registration: string;
  mileage?: number;
  vin?: string;
}

export interface VehicleDataOptions {
  autoTraderMetrics: boolean;
  autoTraderFeatures: boolean;
  autoTraderValuation: boolean;
  capData: boolean;
}

export interface VehicleDataResponse {
  autoTraderValuation?: any;
  autoTraderMetrics?: any;
  autoTraderFeatures?: any;
  capData?: any;
  capVinData?: any;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class InspectCollectVehicleDataService {

  constructor(
    private apiClient: ApiService,
    private data: DataService
  ) {}

  async getVehicleData(request: VehicleDataRequest, options: VehicleDataOptions): Promise<VehicleDataResponse> {
    const response: VehicleDataResponse = {
      errors: []
    };

    const promises: Promise<any>[] = [];

    // AutoTrader Valuation
    if (options.autoTraderValuation) {
      const valuationUrl = `${this.data.apiUrl}/api/inspect-collect/vehicle-data/autotrader/valuation/${request.registration}/odometer/${request.mileage || 0}`;
      promises.push(
        this.apiClient.get({ url: valuationUrl })
          .then(data => response.autoTraderValuation = data)
          .catch(error => response.errors?.push(`AutoTrader Valuation: ${error.message || 'Failed to fetch data'}`))
      );
    }

    // AutoTrader Metrics
    if (options.autoTraderMetrics) {
      const metricsUrl = `${this.data.apiUrl}/api/inspect-collect/vehicle-data/autotrader/metrics/${request.registration}/odometer/${request.mileage || 0}`;
      promises.push(
        this.apiClient.get({ url: metricsUrl })
          .then(data => response.autoTraderMetrics = data)
          .catch(error => response.errors?.push(`AutoTrader Metrics: ${error.message || 'Failed to fetch data'}`))
      );
    }

    // AutoTrader Features
    if (options.autoTraderFeatures) {
      const featuresUrl = `${this.data.apiUrl}/api/inspect-collect/vehicle-data/autotrader/features/${request.registration}`;
      promises.push(
        this.apiClient.get({ url: featuresUrl })
          .then(data => response.autoTraderFeatures = data)
          .catch(error => response.errors?.push(`AutoTrader Features: ${error.message || 'Failed to fetch data'}`))
      );
    }

    // CAP Data
    if (options.capData) {
      const capUrl = `${this.data.apiUrl}/api/inspect-collect/vehicle-data/cap/${request.registration}?odometer=${request.mileage || 0}`;
      promises.push(
        this.apiClient.get({ url: capUrl })
          .then(data => response.capData = data)
          .catch(error => response.errors?.push(`CAP Data: ${error.message || 'Failed to fetch data'}`))
      );

      // CAP VIN Data (if VIN is provided)
      if (request.vin) {
        const capVinUrl = `${this.data.apiUrl}/api/inspect-collect/vehicle-data/cap/vin/${request.vin}?odometer=${request.mileage || 0}`;
        promises.push(
          this.apiClient.get({ url: capVinUrl })
            .then(data => response.capVinData = data)
            .catch(error => response.errors?.push(`CAP VIN Data: ${error.message || 'Failed to fetch data'}`))
        );
      }
    }

    // Wait for all API calls to complete
    await Promise.allSettled(promises);

    return response;
  }
}
